import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Grid,
  Paper,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { api } from "common";
import AlertDialog from "../components/AlertDialog";
import { useNavigate } from "react-router-dom";
import { Country, State, City } from "country-state-city";
import { DEFAULT_FRANCHISE_ID } from "../config/FranchisesConfig";

const Franchise = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const franchiseData = useSelector((state) => state.franchisedata);
  const franchises = franchiseData?.franchises || [];
  const loading = franchiseData?.loading || false;
  const error = franchiseData?.error || { flag: false, msg: null };

  const {
    addFranchise,
    editFranchise,
    deleteFranchise,
    fetchFranchises,
    getFranchiseAdmins,
    addFranchiseAdmin,
    searchUsers,
    removeFranchiseAdmin,
  } = api;

  const [open, setOpen] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [franchiseToDelete, setFranchiseToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    country: "",
    state: "",
    city: "",
    latitude: "",
    longitude: "",
    coverage_radius_km: "",
    status: "active",
    stripe_public_key: "",
    stripe_secret_key: "",
  });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentAdmins, setCurrentAdmins] = useState([]);

  useEffect(() => {
    dispatch(fetchFranchises());
  }, [dispatch]);

  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  useEffect(() => {
    if (formData.country) {
      const countryData = countries.find((c) => c.name === formData.country);
      if (countryData) {
        setStates(State.getStatesOfCountry(countryData.isoCode));
      } else {
        setStates([]);
      }
    } else {
      setStates([]);
    }
  }, [formData.country, countries]);

  useEffect(() => {
    if (formData.country && formData.state) {
      const countryData = countries.find((c) => c.name === formData.country);
      const stateData = states.find((s) => s.name === formData.state);
      if (countryData && stateData) {
        setCities(
          City.getCitiesOfState(countryData.isoCode, stateData.isoCode)
        );
      } else {
        setCities([]);
      }
    } else {
      setCities([]);
    }
  }, [formData.country, formData.state, countries, states]);

  useEffect(() => {
    if (selectedFranchise) {
      const fetchAdminsForFranchise = async () => {
        try {
          const response = await dispatch(
            getFranchiseAdmins(selectedFranchise.id)
          );
          setCurrentAdmins(response);
        } catch (error) {
          console.error("Error fetching admins", error);
        }
      };
      fetchAdminsForFranchise();
    }
  }, [selectedFranchise, dispatch]);

  const handleClickOpen = () => {
    setSelectedFranchise(null);
    setFormData({
      name: "",
      country: "",
      state: "",
      city: "",
      latitude: "",
      longitude: "",
      coverage_radius_km: "",
      status: "active",
      stripe_public_key: "",
      stripe_secret_key: "",
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFranchise(null);
  };

  const handleEdit = (franchise) => {
    setSelectedFranchise(franchise);
    setFormData({
      name: franchise.name || "",
      country: franchise.country || "",
      state: franchise.state || "",
      city: franchise.city || "",
      latitude: franchise.latitude || "",
      longitude: franchise.longitude || "",
      coverage_radius_km: franchise.coverage_radius_km || "",
      status: franchise.status || "active",
      stripe_public_key: franchise.stripe_public_key || "",
      stripe_secret_key: franchise.stripe_secret_key || "",
    });
    setOpen(true);
  };

  const handleDelete = (franchise) => {
    setFranchiseToDelete(franchise);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (franchiseToDelete) {
      dispatch(deleteFranchise(franchiseToDelete.id)).then(() => {
        window.location.reload();
      });
      setDeleteDialogOpen(false);
      setFranchiseToDelete(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (selectedFranchise) {
      dispatch(editFranchise(formData, selectedFranchise.id)).then(() => {
        dispatch(fetchFranchises());
      });
    } else {
      dispatch(addFranchise(formData)).then(() => {
        dispatch(fetchFranchises());
      });
    }
    handleClose();
  };

  const validateForm = () => {
    return (
      formData.name &&
      formData.country &&
      formData.latitude &&
      formData.longitude &&
      formData.coverage_radius_km
    );
  };

  const formatLocation = (franchise) => {
    const locationParts = [
      franchise.city,
      franchise.state,
      franchise.country,
    ].filter(Boolean); // Esto filtra los valores falsy (undefined, null, empty string)

    return locationParts.join(", ");
  };

  // Función para actualizar las coordenadas basadas en la ubicación seleccionada
  const updateCoordinates = (country, state, city) => {
    let location = null;

    if (city && state && country) {
      const countryData = countries.find((c) => c.name === country);
      const stateData = states.find((s) => s.name === state);
      const cityData = cities.find((c) => c.name === city);

      if (cityData) {
        location = {
          latitude: cityData.latitude,
          longitude: cityData.longitude,
        };
      }
    } else if (state && country) {
      const countryData = countries.find((c) => c.name === country);
      const stateData = states.find((s) => s.name === state);

      if (stateData) {
        location = {
          latitude: stateData.latitude,
          longitude: stateData.longitude,
        };
      }
    } else if (country) {
      const countryData = countries.find((c) => c.name === country);

      if (countryData) {
        location = {
          latitude: countryData.latitude,
          longitude: countryData.longitude,
        };
      }
    }

    if (location) {
      setFormData((prev) => ({
        ...prev,
        latitude: location.latitude,
        longitude: location.longitude,
      }));
    }
  };

  // Modificar los handlers de los Autocomplete
  const handleCountryChange = (newValue) => {
    const newCountry = newValue ? newValue.name : "";
    setFormData((prev) => ({
      ...prev,
      country: newCountry,
      state: "",
      city: "",
    }));
    updateCoordinates(newCountry, "", "");
  };

  const handleStateChange = (newValue) => {
    const newState = newValue ? newValue.name : "";
    setFormData((prev) => ({
      ...prev,
      state: newState,
      city: "",
    }));
    updateCoordinates(formData.country, newState, "");
  };

  const handleCityChange = (newValue) => {
    const newCity = newValue ? newValue.name : "";
    setFormData((prev) => ({
      ...prev,
      city: newCity,
    }));
    updateCoordinates(formData.country, formData.state, newCity);
  };

  const handleSearchChange = async (e) => {
    setSearchTerm(e.target?.value);
    if (e.target?.value?.length > 2) {
      try {
        const response = await dispatch(searchUsers(e.target?.value));
        console.log("response handleSearchChange:", response);
        setAdmins(response.data);
      } catch (error) {
        console.error("Error searching users", error);
      }
    } else {
      setAdmins([]);
    }
  };

  const handleAddAdmin = async (userId) => {
    try {
      await dispatch(addFranchiseAdmin(selectedFranchise.id, userId));
      console.log("Usuario añadido como administrador");
      // Actualizar la lista de administradores actuales
      const response = await dispatch(getFranchiseAdmins(selectedFranchise.id));
      setCurrentAdmins(response);
    } catch (error) {
      console.error("Error añadiendo administrador", error);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    try {
      await dispatch(removeFranchiseAdmin(selectedFranchise.id, userId));
      console.log("Administrador eliminado");
      // Actualizar la lista de administradores actuales
      const response = await dispatch(getFranchiseAdmins(selectedFranchise.id));
      setCurrentAdmins(response);
    } catch (error) {
      console.error("Error eliminando administrador", error);
    }
  };

  if (auth.info && auth.info.profile.usertype !== "admin") {
    navigate("/");
    return null;
  }

  // Filtrar las franquicias que se muestran
  const visibleFranchises =
    auth.profile.franchise_id === DEFAULT_FRANCHISE_ID
      ? franchises
      : franchises.filter(
          (franchise) => franchise.id === auth.profile.franchise_id
        );

  return (
    <Paper style={{ padding: 20 }}>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <Typography variant="h4">Franchises</Typography>
          {auth.profile.franchise_id === DEFAULT_FRANCHISE_ID && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleClickOpen}
            >
              Add Franchise
            </Button>
          )}
        </Grid>

        {loading ? (
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <CircularProgress />
          </Grid>
        ) : error?.flag ? (
          <Grid item xs={12}>
            <Typography color="error">
              {error?.msg || "Error loading franchises"}
            </Typography>
          </Grid>
        ) : visibleFranchises && visibleFranchises?.length > 0 ? (
          <Grid item xs={12}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Location</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Coverage Radius</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleFranchises.map((franchise) => (
                    <TableRow key={franchise.id}>
                      <TableCell>{franchise.name}</TableCell>
                      <TableCell>
                        {formatLocation(franchise)}
                        <Typography variant="caption" display="block">
                          {`Lat: ${franchise.latitude}, Long: ${franchise.longitude}`}
                        </Typography>
                      </TableCell>
                      <TableCell>{`${franchise.coverage_radius_km} km`}</TableCell>
                      <TableCell>{franchise.status}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleEdit(franchise)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(franchise)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Typography align="center">No franchises</Typography>
          </Grid>
        )}
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedFranchise ? "Edit Franchise" : "Add Franchise"}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required={auth.profile.franchise_id === DEFAULT_FRANCHISE_ID}
                disabled={auth.profile.franchise_id !== DEFAULT_FRANCHISE_ID}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={countries}
                getOptionLabel={(option) => option.name}
                value={
                  countries.find((c) => c.name === formData.country) || null
                }
                disabled={auth.profile.franchise_id !== DEFAULT_FRANCHISE_ID}
                onChange={(event, newValue) => handleCountryChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    disabled={
                      auth.profile.franchise_id !== DEFAULT_FRANCHISE_ID
                    }
                    {...params}
                    label="Country"
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={states}
                getOptionLabel={(option) => option.name}
                value={states.find((s) => s.name === formData.state) || null}
                onChange={(event, newValue) => handleStateChange(newValue)}
                disabled={
                  auth.profile.franchise_id !== DEFAULT_FRANCHISE_ID ||
                  !formData.country
                }
                renderInput={(params) => (
                  <TextField
                    disabled={
                      auth.profile.franchise_id !== DEFAULT_FRANCHISE_ID
                    }
                    {...params}
                    label="State"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={cities}
                getOptionLabel={(option) => option.name}
                value={cities.find((c) => c.name === formData.city) || null}
                onChange={(event, newValue) => handleCityChange(newValue)}
                disabled={
                  auth.profile.franchise_id !== DEFAULT_FRANCHISE_ID ||
                  !formData.state
                }
                renderInput={(params) => (
                  <TextField
                    disabled={
                      auth.profile.franchise_id !== DEFAULT_FRANCHISE_ID
                    }
                    {...params}
                    label="City"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitude"
                value={formData.latitude}
                InputProps={{
                  readOnly: true,
                }}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitude"
                value={formData.longitude}
                InputProps={{
                  readOnly: true,
                }}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coverage Radius (km)"
                name="coverage_radius_km"
                type="number"
                value={formData.coverage_radius_km}
                onChange={handleInputChange}
                required={auth.profile.franchise_id === DEFAULT_FRANCHISE_ID}
                disabled={auth.profile.franchise_id !== DEFAULT_FRANCHISE_ID}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stripe Public Key"
                name="stripe_public_key"
                value={formData.stripe_public_key}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stripe Secret Key"
                name="stripe_secret_key"
                value={formData.stripe_secret_key}
                onChange={handleInputChange}
              />
            </Grid>
            {selectedFranchise && (
              <Grid item xs={12}>
                <Typography variant="h6">Admins</Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentAdmins?.map((admin) => (
                        <TableRow key={admin.id}>
                          <TableCell>{admin.name}</TableCell>
                          <TableCell>{admin.email}</TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => handleRemoveAdmin(admin.id)}
                            >
                              Eliminar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
            {selectedFranchise && (
              <Grid item xs={12}>
                <Typography variant="h6">Add Admin</Typography>
                <TextField
                  fullWidth
                  label="Search User"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Introduce the name or email of the user"
                  style={{ marginBottom: 10 }}
                />
                {admins?.length > 0 && (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {admins.map((admin) => (
                          <TableRow key={admin.id}>
                            <TableCell>{admin.name}</TableCell>
                            <TableCell>{admin.email}</TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleAddAdmin(admin.id)}
                              >
                                Add as Admin
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!validateForm()}
          >
            {selectedFranchise ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Franchise"
        content="Are you sure you want to delete this franchise?"
        onAgree={confirmDelete}
      />
    </Paper>
  );
};

export default Franchise;
